import React, { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from "@/hooks/ui/use-toast";

interface ResetPasswordFormProps {
  onPasswordSet: () => void;
}

const ResetPasswordForm: React.FC<ResetPasswordFormProps> = ({ onPasswordSet }) => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    if (password.length < 6) {
      toast.error('Password must be at least 6 characters long.', { businessName: "Authentication" });
      setIsSubmitting(false);
      return;
    }

    if (password !== confirmPassword) {
      toast.error('Passwords do not match.', { businessName: "Authentication" });
      setIsSubmitting(false);
      return;
    }

    try {
      const { data, error } = await supabase.auth.updateUser({
        password: password,
      });

      if (error) {
        throw error;
      }

      if (data.user) {
        toast.success('Your password has been updated successfully!', { businessName: "Authentication" });
        onPasswordSet(); // Notify parent component
      }
    } catch (error: unknown) {
      console.error('Error setting new password:', error);
      toast.error('Failed to set new password', {
        description: error instanceof Error ? error.message : String(error) || 'An unexpected error occurred.',
        businessName: "Authentication"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 py-4">
      <div>
        <Label htmlFor="new-password">New Password</Label>
        <Input
          id="new-password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Enter your new password"
          required
        />
      </div>
      <div>
        <Label htmlFor="confirm-new-password">Confirm New Password</Label>
        <Input
          id="confirm-new-password"
          type="password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          placeholder="Confirm your new password"
          required
        />
      </div>
      <Button type="submit" disabled={isSubmitting} className="w-full">
        {isSubmitting ? 'Setting Password...' : 'Set Password'}
      </Button>
    </form>
  );
};

export default ResetPasswordForm;
